import {
  Body,
  Box,
  Flex,
  Heading,
  Image,
  LinkBox,
  LinkOverlay,
  VStack,
} from '@hover/blueprint';
import { GridItem } from '@hover/blueprint/chakra';
import { breakpoints } from '@hover/blueprint/foundation';
import React from 'react';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import cabinetsImage from 'assets/img/designLandingPage/cabinets.jpg';
import designInspirationImage from 'assets/img/designLandingPage/designInspiration.jpg';
import entryDoorsImage from 'assets/img/designLandingPage/entryDoors.jpg';
import flooringImage from 'assets/img/designLandingPage/flooring.jpg';
import garageDoorsImage from 'assets/img/designLandingPage/garageDoors.jpg';
import paintImage from 'assets/img/designLandingPage/paint.jpg';
import roofingImage from 'assets/img/designLandingPage/roofing.jpg';
import sidingImage from 'assets/img/designLandingPage/siding.jpg';
import windowsImage from 'assets/img/designLandingPage/windows.jpg';

import { CustomHorizontalScroll } from './CustomHorizontalScroll';

type Props = {
  onTryClick: () => void;
};

const DesignYourHome: React.FC<Props> = ({ onTryClick }) => {
  const t = useTranslate(TranslationNamespace.DESIGNER_LANDING_PAGE);
  const { trackingData } = useHomeownerAppContext();

  const spaces = [
    {
      title: t('designYourHome.designInspiration.title'),
      description: t('designYourHome.designInspiration.description'),
      image: designInspirationImage,
      isComing: false,
    },
    {
      title: t('designYourHome.paint.title'),
      description: t('designYourHome.paint.description'),
      image: paintImage,
      link: '/paint-color-visualizer',
    },
    {
      title: t('designYourHome.siding.title'),
      description: t('designYourHome.siding.description'),
      image: sidingImage,
      isComing: false,
    },
    {
      title: t('designYourHome.roofing.title'),
      description: t('designYourHome.roofing.description'),
      image: roofingImage,
      isComing: false,
    },
    {
      title: t('designYourHome.flooring.title'),
      description: t('designYourHome.flooring.description'),
      image: flooringImage,
      isComing: false,
    },
    {
      title: t('designYourHome.windows.title'),
      description: t('designYourHome.windows.description'),
      image: windowsImage,
      isComing: false,
    },
    {
      title: t('designYourHome.cabinets.title'),
      description: t('designYourHome.cabinets.description'),
      image: cabinetsImage,
      isComing: false,
    },
    {
      title: t('designYourHome.garageDoors.title'),
      description: t('designYourHome.garageDoors.description'),
      image: garageDoorsImage,
      isComing: true,
    },
    {
      title: t('designYourHome.entryDoors.title'),
      description: t('designYourHome.entryDoors.description'),
      image: entryDoorsImage,
      isComing: true,
    },
  ];

  return (
    <VStack alignItems="center">
      <Box
        alignItems="center"
        justifyContent="space-between"
        marginBottom={{ base: 700, tablet: 900 }}
        marginTop={{ base: 1100, tablet: 1200 }}
        maxWidth={breakpoints.width500}
        paddingX={{ base: 500, tablet: 800 }}
        width="100%"
      >
        <Heading>{t('designYourHome.header')}</Heading>
        <Box display={{ base: 'none', tablet: 'block' }}>
          <Button
            onClick={onTryClick}
            optionalTrackingProperties={{
              ...trackingData,
              primary_cta: true,
              feature: 'homeowner-web-app',
              page_or_screen_name: 'homeowner-app-home',
              track_id: 'designer-lp-features-scroll',
              button_location: 'Features scroll',
            }}
            size="large"
            width="100%"
          >
            {t('tryItWithYourPlace')}
          </Button>
        </Box>
      </Box>

      <Box
        maxWidth={breakpoints.width500}
        paddingX={{ base: 500, tablet: 800, desktop: 0 }}
        width="100%"
      >
        <CustomHorizontalScroll
          scrollBarDisplay={{ base: 'none', desktop: 'block' }}
          scrollBarHorizontalPadding={800}
          thumbSize="2px"
        >
          <Box
            columnGap={{ base: 200, tablet: 500 }}
            display={{ base: 'grid', desktop: 'flex' }}
            gridTemplateColumns={{
              base: 'repeat(2, 1fr)',
              desktop: undefined,
            }}
            marginBottom={{ base: 600, tablet: 900 }}
            rowGap={700}
          >
            {spaces.map(
              ({ title, image, description, link, isComing }, index) => (
                <LinkBox
                  key={title}
                  pl={index === 0 ? { base: 0, desktop: 800 } : 0}
                  pr={
                    index === spaces.length - 1 ? { base: 0, desktop: 800 } : 0
                  }
                >
                  <LinkOverlay
                    href={link}
                    textDecoration="inherit"
                    width="100%"
                  >
                    <GridItem position="relative">
                      {isComing && (
                        <Box
                          backdropFilter="blur(25px)"
                          background="hsla(0,0%,5%,.5)"
                          borderRadius={300}
                          paddingX={300}
                          paddingY={200}
                          position="absolute"
                          right={300}
                          top={300}
                        >
                          <Body as="span" color="neutral.0" isBold size={200}>
                            {t('comingSoon')}
                          </Body>
                        </Box>
                      )}
                      <Image
                        alt={title}
                        borderRadius={400}
                        objectFit="cover"
                        src={image}
                        width={{ base: '100%', desktop: 'auto' }}
                      />
                      <Flex
                        direction="column"
                        marginTop={{ base: 300, tablet: 500 }}
                        whiteSpace="break-spaces"
                      >
                        <Body
                          as="span"
                          isBold
                          marginBottom={{ base: 0, tablet: 300, desktop: 400 }}
                          size={{ base: 200, tablet: 300, desktop: 500 }}
                        >
                          {title}
                        </Body>
                        <Body
                          as="span"
                          color="neutral.550"
                          display={{ base: 'none', tablet: 'block' }}
                          size={{ tablet: 200, desktop: 300 }}
                        >
                          {description}
                        </Body>
                      </Flex>
                    </GridItem>
                  </LinkOverlay>
                </LinkBox>
              ),
            )}
          </Box>
        </CustomHorizontalScroll>
      </Box>
    </VStack>
  );
};

export { DesignYourHome };
